import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Collapse, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import NewsIcon from "@icons/NewsIcon";
import {
  getSomChanges,
  getStateRegulatoryUpdates,
  SomChange,
  DocumentSummary,
  type StateRegulatoryUpdateItem,
} from "@services/discover";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { auditLogService } from "@services/auditLog";
import { getCurrentUserId } from "@utils/userUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { isRhcOrFqhc } from "@utils/isRhcOrFqhc";
import { toUsStateCode } from "@utils/usStateCode";
import {
  filterStateCatalog,
  type StateRegulatoryCatalogEntry,
} from "@data/stateRegulatoryCatalog";
import {
  parseRequirementText,
  type RequirementSegment,
} from "@utils/formatRequirementText";

interface FilterOption {
  id: string;
  label: string;
  selected: boolean;
}

type TabId = "overview" | "requirements" | "guidelines" | "survey" | "documents";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "requirements", label: "Requirements" },
  { id: "guidelines", label: "Guidelines" },
  { id: "survey", label: "Survey" },
  { id: "documents", label: "Documents" },
];

// ─── Styled Components ────────────────────────────────────────────────────────

const ContentArea = styled(Box)({
  flex: "1 1 0",
  padding: "16px 48px",
  overflowY: "auto",
  display: "flex",
  gap: "16px",
  alignItems: "flex-start",
  height: "100%",
});

const CenteredContentArea = styled(Box)({
  flex: 1,
  padding: "16px 48px",
  overflowY: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: "400px",
  height: "100%",
});

const MainContent = styled(Box)({
  flex: "1 1 0",
  minWidth: "0",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  overflowY: "auto",
});

const Sidebar = styled(Box)({
  width: "220px",
  flexShrink: 0,
  maxHeight: "100%",
  overflowY: "auto",
});

const FilterCard = styled(Box)({
  backgroundColor: "#f3feff",
  border: "1px solid #b9e3ed",
  borderRadius: "8px",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

const FilterButton = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
  backgroundColor: selected ? "#1a6c74" : "#eafdff",
  border: "1px solid #99d4e1",
  borderRadius: "8px",
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: selected ? "#156069" : "#d8f0f4" },
}));

const FilterLabel = styled(Typography)<{ selected?: boolean }>(({ selected }) => ({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
  color: selected ? "#e5f5f8" : "#05495a",
  lineHeight: 1.5,
}));

const AccordionWrapper = styled(Box)({
  borderRadius: "10px",
  border: "1px solid #d1eaed",
  overflow: "hidden",
  backgroundColor: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
});

const AccordionHeader = styled(Box)<{ $expanded?: boolean }>(({ $expanded }) => ({
  backgroundColor: $expanded ? "#f0fbfd" : "#fff",
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": { backgroundColor: "#f0fbfd" },
}));

const ChevronIcon = styled(Box)<{ $expanded?: boolean }>(({ $expanded }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s ease",
  transform: $expanded ? "rotate(180deg)" : "rotate(0deg)",
  flexShrink: 0,
}));

const FederalBadge = styled(Box)({
  fontFamily: '"Rethink Sans", sans-serif',
  backgroundColor: "#0a829d",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 600,
  borderRadius: "999px",
  padding: "4px 14px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  letterSpacing: "0.01em",
});

const StateBadge = styled(Box)({
  fontFamily: '"Rethink Sans", sans-serif',
  backgroundColor: "#0a829d",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 600,
  borderRadius: "999px",
  padding: "4px 14px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  letterSpacing: "0.01em",
});

const ExpandedContent = styled(Box)({
  padding: "20px",
  backgroundColor: "#fff",
  borderTop: "1px solid #d1eaed",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
});

/** Full-width pill tab bar — matches Figma */
const TabBar = styled(Box)({
  display: "flex",
  backgroundColor: "#efefef",
  borderRadius: "999px",
  padding: "4px",
  gap: "2px",
  width: "100%",
});

const Tab = styled(Box)<{ $active?: boolean }>(({ $active }) => ({
  fontFamily: '"Rethink Sans", sans-serif',
  flex: 1,
  textAlign: "center",
  padding: "8px 0",
  borderRadius: "999px",
  fontSize: "15px",
  fontWeight: $active ? 600 : 400,
  color: $active ? "#0a829d" : "#6b7280",
  backgroundColor: $active ? "#ffffff" : "transparent",
  cursor: "pointer",
  transition: "all 0.15s ease",
  whiteSpace: "nowrap",
  letterSpacing: "-0.1px",
  boxShadow: $active ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
  "&:hover": { color: $active ? "#0a829d" : "#374151" },
}));

/** 3-column info grid matching Figma */
const InfoGrid = styled(Box)({
  border: "0.5px solid #99D4E1",
  borderRadius: "10px",
  overflow: "hidden",
  backgroundColor: "#FBFDFD",
});

const InfoGridRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  "&:not(:last-child)": { borderBottom: "0.5px solid #99D4E1" },
});

const InfoCell = styled(Box)({
  padding: "14px 18px",
  backgroundColor: "#FBFDFD",
  "&:not(:last-child)": { borderRight: "0.5px solid #99D4E1" },
});

const InfoLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  color: "#666666",
  lineHeight: "16px",
  marginBottom: "5px",
  letterSpacing: "0px",
});

const InfoValue = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  color: "#333333",
  lineHeight: "20px",
  letterSpacing: "-0.15px",
});

const SectionTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "16px",
  fontWeight: 700,
  color: "#111827",
  paddingBottom: "10px",
  marginBottom: "14px",
  borderBottom: "0.5px solid #99D4E1",
  letterSpacing: "-0.2px",
});

const EmptyState = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  width: "100%",
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateString: string | null): string {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

/**
 * Renders structured regulatory requirement text (AB#10970).
 * Parsing/normalization lives in `@utils/formatRequirementText`.
 */
function formatRequirementText(text: string): React.ReactNode {
  const segments = parseRequirementText(text);
  if (segments.length === 0) return null;

  const bodySx = {
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: "14px",
    lineHeight: 1.8,
    color: "#3b3b3b",
    overflowWrap: "anywhere" as const,
    wordBreak: "break-word" as const,
    maxWidth: "100%",
  };

  const markerSx = {
    fontSize: "13px",
    fontWeight: 700,
    color: "#065B70",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    pt: "3px",
    userSelect: "none" as const,
  };

  const subMarkerSx = { ...markerSx, color: "#3095AB", minWidth: "28px" };

  const renderHeading = (seg: RequirementSegment, key: React.Key) => (
    <Box key={key} sx={{ mt: 1, pt: 1, borderTop: "1px solid #CFF7FF" }}>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 700,
          color: "#065B70",
          mb: 0.5,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          overflowWrap: "anywhere",
        }}
      >
        {seg.text}
      </Typography>
    </Box>
  );

  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < segments.length) {
    const seg = segments[i];
    if (seg.kind === "heading") {
      nodes.push(renderHeading(seg, `h-${i}`));
      i += 1;
      continue;
    }
    if (seg.kind === "roman") {
      const letters: RequirementSegment[] = [];
      let j = i + 1;
      while (j < segments.length && segments[j].kind === "letter") {
        letters.push(segments[j]);
        j += 1;
      }
      nodes.push(
        <Box key={`r-${i}`} sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <Typography component="span" sx={{ ...markerSx, minWidth: "32px" }}>
            ({seg.label})
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {seg.text ? <Typography sx={{ ...bodySx, mb: letters.length ? 0.5 : 0 }}>{seg.text}</Typography> : null}
            {letters.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mt: seg.text ? 0.5 : 0 }}>
                {letters.map((letter, li) => (
                  <Box key={`l-${i}-${li}`} sx={{ display: "flex", gap: "8px", pl: 1, alignItems: "flex-start" }}>
                    <Typography component="span" sx={subMarkerSx}>({letter.label})</Typography>
                    <Typography sx={{ ...bodySx, flex: 1, minWidth: 0 }}>{letter.text}</Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>
      );
      i = j;
      continue;
    }
    if (seg.kind === "letter") {
      nodes.push(
        <Box key={`lo-${i}`} sx={{ display: "flex", gap: "8px", pl: 4, alignItems: "flex-start" }}>
          <Typography component="span" sx={subMarkerSx}>({seg.label})</Typography>
          <Typography sx={{ ...bodySx, flex: 1, minWidth: 0 }}>{seg.text}</Typography>
        </Box>
      );
      i += 1;
      continue;
    }
    if (seg.kind === "circled") {
      nodes.push(
        <Box key={`c-${i}`} sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <Typography component="span" sx={{ ...markerSx, minWidth: "28px" }}>{seg.label}</Typography>
          <Typography sx={{ ...bodySx, flex: 1, minWidth: 0 }}>{seg.text}</Typography>
        </Box>
      );
      i += 1;
      continue;
    }
    if (seg.kind === "bullet") {
      nodes.push(
        <Box key={`b-${i}`} sx={{ display: "flex", gap: "8px", pl: 2, alignItems: "flex-start" }}>
          <Typography component="span" sx={{ ...markerSx, color: "#3095AB", minWidth: "16px" }}>•</Typography>
          <Typography sx={{ ...bodySx, flex: 1, minWidth: 0 }}>{seg.text}</Typography>
        </Box>
      );
      i += 1;
      continue;
    }
    nodes.push(
      <Typography key={`p-${i}`} sx={{ ...bodySx }}>{seg.text}</Typography>
    );
    i += 1;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", minWidth: 0, overflowWrap: "anywhere" }}>
      {nodes}
    </Box>
  );
}


/** Unified State card row for API + curated catalog (AB#10987). */
export type StateUpdateDisplayRow = {
  id: string;
  title: string;
  topics: string[];
  source: string;
  url: string;
  lastRefreshed: string;
};

function formatLastRefreshed(value: string | null | undefined): string {
  if (!value) return "N/A";
  const trimmed = String(value).trim();
  if (!trimmed) return "N/A";
  // Prefer YYYY-MM-DD when ISO datetime
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  try {
    return new Date(trimmed).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return trimmed;
  }
}

export function mapApiItemToStateRow(item: StateRegulatoryUpdateItem): StateUpdateDisplayRow {
  return {
    id: `api-${item.id}`,
    title: item.title,
    topics: Array.isArray(item.topics) ? item.topics.filter(Boolean) : [],
    source: item.source || "",
    url: item.url,
    lastRefreshed: formatLastRefreshed(item.last_refreshed_at ?? item.published_at),
  };
}

export function mapCatalogToStateRow(entry: StateRegulatoryCatalogEntry): StateUpdateDisplayRow {
  return {
    id: entry.id,
    title: entry.title,
    topics: [...entry.topics],
    source: entry.source,
    url: entry.url,
    lastRefreshed: entry.lastRefreshed,
  };
}

/**
 * Resolve State rows: API ≥1 → API; API 0 / error → curated catalog; still empty → [].
 */
export function resolveStateUpdateRows(
  apiItems: StateRegulatoryUpdateItem[] | null,
  catalogRows: StateRegulatoryCatalogEntry[],
): { rows: StateUpdateDisplayRow[]; fromApi: boolean } {
  if (apiItems && apiItems.length >= 1) {
    return { rows: apiItems.map(mapApiItemToStateRow), fromApi: true };
  }
  return { rows: catalogRows.map(mapCatalogToStateRow), fromApi: false };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export interface LatestUpdatesProps {
  initialSomTag?: string;
  initialSectionId?: number;
}

export default function LatestUpdates({ initialSomTag, initialSectionId }: LatestUpdatesProps) {
  const [updates, setUpdates] = useState<SomChange[]>([]);
  const [documentSummaries, setDocumentSummaries] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [selectedCountry, setSelectedCountry] = useState<string>(getSelectedCountryCode());
  const [selectedFacility, setSelectedFacility] = useState<{ facility_type_slug?: string; state?: string } | null>(
    getSelectedFacilityFromSession()
  );
  const [filters, setFilters] = useState<FilterOption[]>([
    { id: "federal", label: "Federal", selected: true },
    { id: "state", label: "State", selected: false },
    { id: "other", label: "Other", selected: false },
  ]);
  const [stateRows, setStateRows] = useState<StateUpdateDisplayRow[]>([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateEmptyMessage, setStateEmptyMessage] = useState<string | null>(null);

  const summaryByAppendix = useMemo(() => {
    const map = new Map<string, { facility_type_title: string | null; topic: string | null }>();
    for (const s of documentSummaries) {
      if (s.appendix_letter && !map.has(s.appendix_letter)) {
        map.set(s.appendix_letter, { facility_type_title: s.facility_type_title ?? null, topic: s.topic ?? null });
      }
    }
    return map;
  }, [documentSummaries]);

  useEffect(() => {
    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId, endpoint: "/dashboard/discover", method: "GET",
        request_payload: { tab: "Regulatory Updates" }, response_payload: {},
        status_code: 200,
        facility_info: facility ? { facility_id: facility.facility_id, facility_name: facility.facility_name } : {},
        action_details: { page: "Discover", tab: "Regulatory Updates", action: "VIEW" },
        action_type: "VIEW", action_status: "SUCCESS",
      });
    };
    logPageAccess();
  }, []);

  useEffect(() => {
    const facility = getSelectedFacilityFromSession();
    fetchUpdates(facility?.facility_type_slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkCountryChange = () => {
      const currentCountry = getSelectedCountryCode();
      if (currentCountry !== selectedCountry) {
        setSelectedCountry(currentCountry);
        fetchUpdates(getSelectedFacilityFromSession()?.facility_type_slug);
      }
    };
    checkCountryChange();
    window.addEventListener("storage", checkCountryChange);
    const interval = setInterval(checkCountryChange, 1000);
    return () => { clearInterval(interval); window.removeEventListener("storage", checkCountryChange); };
  }, [selectedCountry]);

  useEffect(() => {
    const checkFacilityChange = () => {
      const currentFacility = getSelectedFacilityFromSession() as {
        facility_type_slug?: string;
        state?: string;
      } | null;
      if (
        currentFacility?.facility_type_slug !== selectedFacility?.facility_type_slug ||
        currentFacility?.state !== selectedFacility?.state
      ) {
        setSelectedFacility(currentFacility);
        fetchUpdates(currentFacility?.facility_type_slug);
      }
    };
    const interval = setInterval(checkFacilityChange, 1000);
    window.addEventListener("storage", checkFacilityChange);
    return () => { clearInterval(interval); window.removeEventListener("storage", checkFacilityChange); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility?.facility_type_slug, selectedFacility?.state]);

  useEffect(() => {
    if (!updates.length) return;
    if (initialSectionId != null) { setExpandedId(String(initialSectionId)); return; }
    if (initialSomTag?.trim()) {
      const normalized = initialSomTag.trim().toUpperCase();
      const match = updates.find((u) => (u.tag || "").trim().toUpperCase() === normalized);
      if (match) setExpandedId(String(match.section_id));
    }
  }, [updates, initialSomTag, initialSectionId]);

  const isStateSelected = filters.find((f) => f.id === "state")?.selected;
  const isOtherSelected = filters.find((f) => f.id === "other")?.selected;

  // State filter — Rules API with curated JSON fallback (AB#10987)
  useEffect(() => {
    if (!isStateSelected) return;

    const facility = (selectedFacility ??
      getSelectedFacilityFromSession()) as {
      facility_type_slug?: string;
      state?: string;
    } | null;
    const slug = facility?.facility_type_slug;

    if (!isRhcOrFqhc(slug)) {
      setStateRows([]);
      setStateEmptyMessage(null);
      setStateLoading(false);
      return;
    }

    const code = toUsStateCode(facility?.state);
    const catalogRows = filterStateCatalog(code, slug);
    let cancelled = false;

    const run = async () => {
      setStateLoading(true);
      setStateEmptyMessage(null);

      if (!code) {
        // No usable state → curated (empty) → empty message, never Coming Soon
        if (!cancelled) {
          setStateRows([]);
          setStateEmptyMessage(
            `No curated state resources for ${facility?.state || "this state"} yet.`,
          );
          setStateLoading(false);
        }
        return;
      }

      let apiItems: StateRegulatoryUpdateItem[] | null = null;
      try {
        const response = await getStateRegulatoryUpdates({
          state_code: code,
          facility_type_slug: slug || undefined,
          limit: 50,
        });
        apiItems = response.items ?? [];
      } catch (err) {
        console.error("Error fetching state regulatory updates:", err);
        apiItems = null; // error → curated fallback
      }

      if (cancelled) return;

      const { rows } = resolveStateUpdateRows(apiItems, catalogRows);
      setStateRows(rows);
      if (rows.length === 0) {
        setStateEmptyMessage(
          `No curated state resources for ${facility?.state || "this state"} yet.`,
        );
      } else {
        setStateEmptyMessage(null);
      }
      setStateLoading(false);
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStateSelected,
    selectedFacility?.facility_type_slug,
    selectedFacility?.state,
  ]);

  const fetchUpdates = async (facilityTypeSlug?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSomChanges({
        change_type: "updated",
        limit: 10,
        facility_type_slug: facilityTypeSlug?.toLowerCase() || undefined,
      });
      setUpdates(response.result.changes || []);
      setDocumentSummaries(response.result.document_summaries ?? []);
    } catch (err) {
      console.error("Error fetching SOM changes:", err);
      setError("Failed to load Regulatory Updates. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterToggle = (filterId: string) => {
    setFilters((prev) => prev.map((f) => ({ ...f, selected: f.id === filterId })));
  };

  const handleCardClick = (sectionId: number) => {
    const id = sectionId.toString();
    const isOpening = expandedId !== id;
    setExpandedId(isOpening ? id : null);
    if (isOpening) setActiveTab("overview");
  };

  const renderChevron = (expanded: boolean) => (
    <ChevronIcon $expanded={expanded}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" stroke="#065B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </ChevronIcon>
  );

  const renderIcon = (id: string) => {
    if (id === "federal") return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 14V6L8 2L14 6V14M2 14H14M5 14V9H11V14M6 5H7M9 5H10M6 7H7M9 7H10" stroke="#05495a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    if (id === "state") return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M10.1203 5.16352L7.24953 8.04196L5.85355 6.64599L5.78431 6.58813C5.58944 6.45314 5.32001 6.47242 5.14645 6.64599C4.95118 6.84125 4.95118 7.15783 5.14645 7.35309L6.89645 9.10309L6.96576 9.161C7.16082 9.29608 7.43053 9.27658 7.60403 9.10262L10.8283 5.86968L10.8861 5.80036C11.0208 5.60531 11.0012 5.33591 10.8274 5.16258C10.6319 4.96758 10.3153 4.968 10.1203 5.16352ZM9.15886 14.5577C10.9028 13.0162 14 9.8739 14 7C14 3.68629 11.3137 1 8 1C4.68629 1 2 3.68629 2 7C2 9.8739 5.09724 13.0162 6.84114 14.5577C7.5095 15.1485 8.4905 15.1485 9.15886 14.5577ZM8 2C10.7614 2 13 4.23858 13 7C13 8.10846 12.3854 9.39535 11.4303 10.6827C10.4963 11.9415 9.34306 13.0602 8.49657 13.8085C8.20644 14.065 7.79356 14.065 7.50343 13.8085C6.65694 13.0602 5.50366 11.9415 4.56972 10.6827C3.61464 9.39535 3 8.10846 3 7C3 4.23858 5.23858 2 8 2Z" fill="#262626" />
      </svg>
    );
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2.5 1C1.67157 1 1 1.67157 1 2.5V7.5C1 8.32843 1.67157 9 2.5 9H5.5C6.32843 9 7 8.32843 7 7.5V2.5C7 1.67157 6.32843 1 5.5 1H2.5ZM2 2.5C2 2.22386 2.22386 2 2.5 2H5.5C5.77614 2 6 2.22386 6 2.5V7.5C6 7.77614 5.77614 8 5.5 8H2.5C2.22386 8 2 7.77614 2 7.5V2.5ZM10.5 7C9.67157 7 9 7.67157 9 8.5V13.5C9 14.3284 9.67157 15 10.5 15H13.5C14.3284 15 15 14.3284 15 13.5V8.5C15 7.67157 14.3284 7 13.5 7H10.5ZM10 8.5C10 8.22386 10.2239 8 10.5 8H13.5C13.7761 8 14 8.22386 14 8.5V13.5C14 13.7761 13.7761 14 13.5 14H10.5C10.2239 14 10 13.7761 10 13.5V8.5ZM9 2.5C9 1.67157 9.67157 1 10.5 1H13.5C14.3284 1 15 1.67157 15 2.5V3.5C15 4.32843 14.3284 5 13.5 5H10.5C9.67157 5 9 4.32843 9 3.5V2.5ZM10.5 2C10.2239 2 10 2.22386 10 2.5V3.5C10 3.77614 10.2239 4 10.5 4H13.5C13.7761 4 14 3.77614 14 3.5V2.5C14 2.22386 13.7761 2 13.5 2H10.5ZM2.5 11C1.67157 11 1 11.6716 1 12.5V13.5C1 14.3284 1.67157 15 2.5 15H5.5C6.32843 15 7 14.3284 7 13.5V12.5C7 11.6716 6.32843 11 5.5 11H2.5ZM2 12.5C2 12.2239 2.22386 12 2.5 12H5.5C5.77614 12 6 12.2239 6 12.5V13.5C6 13.7761 5.77614 14 5.5 14H2.5C2.22386 14 2 13.7781 2 13.5V12.5Z" fill="#05495A" />
      </svg>
    );
  };

  const renderTabContent = (update: SomChange, tab: TabId) => {
    if (tab === "overview") {
      return (
        <Box>
          <SectionTitle>Change Information</SectionTitle>
          <InfoGrid>
            <InfoGridRow>
              <InfoCell>
                <InfoLabel>Revision</InfoLabel>
                <InfoValue>{update.revision || "N/A"}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>Issued Date</InfoLabel>
                <InfoValue>{formatDate(update.issued_date)}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>Effective Date</InfoLabel>
                <InfoValue>{formatDate(update.effective_date)}</InfoValue>
              </InfoCell>
            </InfoGridRow>
            <InfoGridRow>
              <InfoCell>
                <InfoLabel>Implementation Date</InfoLabel>
                <InfoValue>{formatDate(update.implementation_date)}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>Paragraph Identifier :</InfoLabel>
                <InfoValue>{update.paragraph_identifier || "N/A"}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>Active</InfoValue>
              </InfoCell>
            </InfoGridRow>
          </InfoGrid>
        </Box>
      );
    }

    if (tab === "requirements") {
      if (!update.requirement_text) {
        return <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>No requirements available.</Typography>;
      }
      return (
        <Box>
          <SectionTitle>Requirement</SectionTitle>
          {formatRequirementText(update.requirement_text)}
        </Box>
      );
    }

    if (tab === "guidelines") {
      if (!update.interpretative_guidelines?.length) {
        return <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>No guidelines available.</Typography>;
      }
      return (
        <Box>
          <SectionTitle>Interpretative Guidelines</SectionTitle>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {update.interpretative_guidelines.map((guideline, idx) => (
              <Box key={idx}>{formatRequirementText(guideline)}</Box>
            ))}
          </Box>
        </Box>
      );
    }

    if (tab === "survey") {
      if (!update.survey_procedures?.length) {
        return <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>No survey procedures available.</Typography>;
      }
      return (
        <Box>
          <SectionTitle>Survey Procedures</SectionTitle>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {update.survey_procedures.map((procedure, idx) => (
              <Box key={idx}>{formatRequirementText(procedure)}</Box>
            ))}
          </Box>
        </Box>
      );
    }

    if (tab === "documents") {
      const hasDoc = update.file_path || update.document_date || update.url;
      if (!hasDoc) {
        return <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>No documents available.</Typography>;
      }
      return (
        <Box>
          <SectionTitle>Documents</SectionTitle>
          <InfoGrid>
            <InfoGridRow>
              {update.file_path && (
                <InfoCell>
                  <InfoLabel>File</InfoLabel>
                  <InfoValue>{update.file_path.split("/").pop()}</InfoValue>
                </InfoCell>
              )}
              {update.document_date && (
                <InfoCell>
                  <InfoLabel>Document Date</InfoLabel>
                  <InfoValue>{formatDate(update.document_date)}</InfoValue>
                </InfoCell>
              )}
              {update.url && (
                <InfoCell>
                  <InfoLabel>Source</InfoLabel>
                  <InfoValue>
                    <a href={update.url} target="_blank" rel="noopener noreferrer"
                      style={{ color: "#0a829d", textDecoration: "underline", fontSize: "14px" }}
                      onClick={(e) => e.stopPropagation()}>
                      View Document
                    </a>
                  </InfoValue>
                </InfoCell>
              )}
            </InfoGridRow>
          </InfoGrid>
        </Box>
      );
    }

    return null;
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <CenteredContentArea>
        <EmptyState>
          <CircularProgress sx={{ color: "#0a829d" }} />
          <Typography sx={{ color: "#3b3b3b", fontSize: "16px" }}>Loading regulatory updates...</Typography>
        </EmptyState>
      </CenteredContentArea>
    );
  }

  if (error) {
    return (
      <CenteredContentArea>
        <EmptyState>
          <Typography sx={{ color: "#d32f2f", fontSize: "16px", textAlign: "center" }}>{error}</Typography>
        </EmptyState>
      </CenteredContentArea>
    );
  }

  const renderFilterSidebar = () => (
    <Sidebar>
      <FilterCard>
        {filters.map((filter) => (
          <FilterButton key={filter.id} selected={filter.selected} onClick={() => handleFilterToggle(filter.id)}>
            {renderIcon(filter.id)}
            <FilterLabel selected={filter.selected}>{filter.label}</FilterLabel>
          </FilterButton>
        ))}
      </FilterCard>
    </Sidebar>
  );

  const sourceLabel = (source: string) => {
    const s = (source || "").toLowerCase();
    if (s === "rhihub") return "RHIhub";
    if (s === "source_on_healthcare") return "Source on Healthcare";
    return source || "Source";
  };

  // Other filter — Coming Soon unchanged
  if (isOtherSelected) {
    return (
      <ContentArea>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "32px", fontWeight: 600, textAlign: "center" }}>Coming Soon</Typography>
        </Box>
        {renderFilterSidebar()}
      </ContentArea>
    );
  }

  // State filter — RHC/FQHC Rules API + curated fallback; others Coming Soon
  if (isStateSelected) {
    const facility = (selectedFacility ??
      getSelectedFacilityFromSession()) as {
      facility_type_slug?: string;
      state?: string;
    } | null;
    const slug = facility?.facility_type_slug;

    if (!isRhcOrFqhc(slug)) {
      return (
        <ContentArea>
          <Box
            data-testid="state-coming-soon"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              px: 3,
            }}
          >
            <Typography sx={{ fontSize: "28px", fontWeight: 600, textAlign: "center" }}>
              Coming Soon
            </Typography>
            <Typography sx={{ fontSize: "15px", color: "#4b5563", textAlign: "center", maxWidth: 480, lineHeight: 1.6 }}>
              State regulatory resources are available first for Rural Health Clinics and FQHCs.
              Switch to an RHC or FQHC facility, or check back later.
            </Typography>
          </Box>
          {renderFilterSidebar()}
        </ContentArea>
      );
    }

    if (stateLoading) {
      return (
        <ContentArea>
          <Box
            data-testid="state-loading"
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 240,
            }}
          >
            <EmptyState>
              <CircularProgress sx={{ color: "#0a829d" }} />
              <Typography sx={{ color: "#3b3b3b", fontSize: "16px" }}>
                Loading state regulatory updates...
              </Typography>
            </EmptyState>
          </Box>
          {renderFilterSidebar()}
        </ContentArea>
      );
    }

    const rows = stateRows;

    return (
      <ContentArea>
        <MainContent>
          {rows.length === 0 ? (
            <Box
              data-testid="state-empty"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240,
                px: 2,
              }}
            >
              <Typography sx={{ fontSize: "16px", color: "#3b3b3b", textAlign: "center" }}>
                {stateEmptyMessage ||
                  `No curated state resources for ${facility?.state || "this state"} yet.`}
              </Typography>
            </Box>
          ) : (
            rows.map((row) => (
              <AccordionWrapper key={row.id} data-testid="state-regulatory-card">
                <AccordionHeader
                  $expanded={false}
                  onClick={() => window.open(row.url, "_blank", "noopener,noreferrer")}
                >
                  <Box sx={{ width: 20, height: 20, flexShrink: 0, color: "#065B70" }}>
                    <NewsIcon size={20} color="#065B70" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "#111827",
                        letterSpacing: "-0.1px",
                      }}
                    >
                      {row.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: "13px",
                        color: "#6b7280",
                        mt: 0.5,
                      }}
                    >
                      {[...row.topics, sourceLabel(row.source)].filter(Boolean).join(" · ")}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: "12px",
                        color: "#9ca3af",
                        mt: 0.5,
                      }}
                    >
                      Updated {row.lastRefreshed}
                    </Typography>
                    <Typography
                      component="a"
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{
                        display: "inline-block",
                        mt: 1,
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: "13px",
                        color: "#0a829d",
                        textDecoration: "underline",
                        fontWeight: 600,
                      }}
                    >
                      Open resource
                    </Typography>
                  </Box>
                  <StateBadge>State</StateBadge>
                </AccordionHeader>
              </AccordionWrapper>
            ))
          )}
        </MainContent>
        {renderFilterSidebar()}
      </ContentArea>
    );
  }

  if (updates.length === 0) {
    return (
      <CenteredContentArea>
        <EmptyState>
          <NewsIcon size={48} color="#99d4e1" />
          <Typography sx={{ color: "#3b3b3b", fontSize: "16px", textAlign: "center" }}>No updates available at this time.</Typography>
        </EmptyState>
      </CenteredContentArea>
    );
  }

  return (
    <ContentArea>
      <MainContent>
        {updates.map((update) => {
          const isExpanded = expandedId === update.section_id.toString();
          const summary = update.appendix_letter ? summaryByAppendix.get(update.appendix_letter) : undefined;
          const titleParts = [
            update.tag || update.section_id.toString().padStart(5, "0"),
            update.appendix_letter || null,
            update.paragraph_identifier || null,
          ].filter(Boolean).join(" | ");

          return (
            <AccordionWrapper key={update.section_id}>
              <AccordionHeader $expanded={isExpanded} onClick={() => handleCardClick(update.section_id)}>
                <Box sx={{ width: 20, height: 20, flexShrink: 0, color: "#065B70" }}>
                  <NewsIcon size={20} color="#065B70" />
                </Box>

                <Typography sx={{ fontFamily: '"Rethink Sans", sans-serif', flex: 1, fontSize: "15px", fontWeight: 500, color: "#111827", minWidth: 0, letterSpacing: "-0.1px" }}>
                  {titleParts}
                  {summary && (summary.facility_type_title || summary.topic) && (
                    <Typography component="span" sx={{ fontFamily: '"Rethink Sans", sans-serif', fontSize: "13px", color: "#6b7280", ml: 1 }}>
                      · {[summary.facility_type_title, summary.topic].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Typography>

                {isExpanded && <FederalBadge>Federal</FederalBadge>}

                {renderChevron(isExpanded)}
              </AccordionHeader>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <ExpandedContent>
                  {/* Tab bar */}
                  <TabBar>
                    {TABS.map((tab) => (
                      <Tab
                        key={tab.id}
                        $active={activeTab === tab.id}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.label}
                      </Tab>
                    ))}
                  </TabBar>

                  {/* Tab content */}
                  {renderTabContent(update, activeTab)}
                </ExpandedContent>
              </Collapse>
            </AccordionWrapper>
          );
        })}
      </MainContent>

      <Sidebar>
        <FilterCard>
          {filters.map((filter) => (
            <FilterButton key={filter.id} selected={filter.selected} onClick={() => handleFilterToggle(filter.id)}>
              {renderIcon(filter.id)}
              <FilterLabel selected={filter.selected}>{filter.label}</FilterLabel>
            </FilterButton>
          ))}
        </FilterCard>
      </Sidebar>
    </ContentArea>
  );
}
